import { useCallback, useMemo, useState } from 'react'
import { useAppDispatch, useAppSelector } from '@hooks/reduxHooks'
import {
  OrderDto,
  PrecotationDto,
  CreateBillDTO,
  CreateInvoiceOnlyDto,
  MemberSituationDTO,
  InvoiceDto,
  CCAMDto,
  NGAPDto,
} from '@services/dtos'
import {
  doValidatePrecotations,
  doCreateBill,
  doCreateInvoiceOnly,
  doGetMemberSituation,
  doRemovePrecotation,
  setPrecotation,
  setValidation,
  removeUnfinishedPrecot,
} from '@state/reducers/billingReducer'
import { findCcam, findNgap } from '@state/thunks/procedureThunk'
import { enqueueSnackbar } from '@state/reducers/alertReducer'
import { getCartVitalUrl } from '@state/thunks/cardReadThunk'
import { setReaderId } from '@state/reducers/patientsReducer'
import { getInvoice } from '@state/reducers/invoiceReducer'
import { BillingService } from '@services/BillingService'
import {
  groupPrecotationsByMedicalOrder,
  createCCAMPrecotations,
  createNGAPPrecotations,
  shouldUpdateSituationAmo,
  getUniqueCotationCodes,
  createPrecotationRow,
} from '@utils/billing.utils'

import { BillModes } from '../utils/constants'
import moment from 'moment'

export interface UseBillingWorkflowOptions {
  order: OrderDto
  onBillSuccess?: (result: {
    invoices: InvoiceDto[]
    shouldShowBillStatusDialog: boolean
    autoSetPaid: boolean
  }) => void
  onInvoiceCreated?: () => void
}

export interface UseBillingWorkflowReturn {
  // State
  isLoading: boolean
  isValidating: boolean
  isCreatingBill: boolean
  billUrl: string
  codeSituationSelectorUrl: string
  billMode: string
  billModesOptions: { value: number; label: string; disabled: boolean }[]

  // Actions
  loadPrecotations: () => Promise<void>
  savePrecotations: (rowsToSave?: PrecotationDto[]) => Promise<PrecotationDto[]>
  validatePrecotations: () => Promise<void>
  handleBillModeClick: (
    mode: number,
    rowsToSave?: PrecotationDto[],
  ) => Promise<void>
  handleCreateBill: () => Promise<void>
  handleBillSuccess: (data: any) => Promise<void>
  handleInvoiceOnly: () => Promise<boolean>
  addPrecotationRow: (medicalOrderId: number) => void
  deletePrecotationRow: (precotation: PrecotationDto) => Promise<void>

  // Setters
  setBillUrl: (url: string) => void
  setCodeSituationSelectorUrl: (url: string) => void
  closeSummary: () => void

  // Helpers
  ensureMemberSituation: () => Promise<MemberSituationDTO | null>
  ensureAllPrecotations: () => Promise<boolean>
}

export const useBillingWorkflow = (
  options: UseBillingWorkflowOptions,
): UseBillingWorkflowReturn => {
  const { order, onBillSuccess, onInvoiceCreated } = options
  const dispatch = useAppDispatch()

  // Local state
  const [isLoading, setIsLoading] = useState(false)
  const [isValidating, setIsValidating] = useState(false)
  const [isCreatingBill, setIsCreatingBill] = useState(false)
  const [billUrl, setBillUrl] = useState('')
  const [codeSituationSelectorUrl, setCodeSituationSelectorUrl] = useState('')
  const [billMode, setBillMode] = useState('')

  // Redux state
  const {
    precotations,
    readerId,
    couverture,
    memberSituation,
    myCashRegister,
    invoice,
    cardReaderInsertedCards,
  } = useAppSelector(({ billing, patients, cashRegister, invoice, app }) => ({
    precotations: billing.precotation,
    validation: billing.validation,
    readerId: patients.patientCardReaderId,
    couverture: patients.patientCouverture,
    memberSituation: billing.memberSituation,
    myCashRegister: cashRegister.mine,
    invoice: invoice.invoice,
    cardReaderInsertedCards: app.cardReaderInsertedCards,
  }))

  // Helper: Get reader ID
  const getReaderId = useCallback(async (): Promise<string> => {
    if (readerId) return readerId
    try {
      const res = await dispatch(getCartVitalUrl()).unwrap()
      dispatch(setReaderId(res.id || ''))
      return res.id
    } catch {
      throw new Error('Erreur lors de la récupération de l’id du lecteur')
    }
  }, [dispatch, readerId])

  // Helper: Ensure member situation is loaded
  const ensureMemberSituation =
    useCallback(async (): Promise<MemberSituationDTO | null> => {
      if (memberSituation) return memberSituation
      if (!order.attendingDoctor?.id) return null
      try {
        return await dispatch(
          doGetMemberSituation(order.attendingDoctor.id),
        ).unwrap()
      } catch {
        return null
      }
    }, [dispatch, memberSituation, order.attendingDoctor?.id])

  // Helper: Check if reading type is APCV
  const isReadingTypeApcv = useCallback(async (): Promise<boolean> => {
    const readingTypes = await BillingService.getSesamReadingTypes()
    const apcvType = readingTypes.find((rt) => rt.libelle === 'Apcv')
    return couverture?.typeLecture === apcvType?.code
  }, [couverture?.typeLecture])

  const ensureInitialPrecotationsPerMedicalOrder = useCallback(
    async (loadedPrecotations: PrecotationDto[]) => {
      const grouped = groupPrecotationsByMedicalOrder(
        loadedPrecotations.filter((p) => p.cotation_type !== 'FT'),
      )
      const generated: PrecotationDto[] = []
      const ccamCache = new Map<string, Promise<CCAMDto[]>>()
      const ngapCache = new Map<string, Promise<NGAPDto[]>>()

      const getExamDate = (date?: Date | string) => {
        const fallback = order.registeredAt ?? new Date()
        const value = moment(date ?? fallback)
        return value.isValid() ? value.toDate() : moment(fallback).toDate()
      }

      for (const medicalOrder of order.medicalOrders) {
        const existing = grouped.get(medicalOrder.id) ?? []
        if (existing.length > 0) continue

        const { exam } = medicalOrder
        if (!exam) continue

        const examDate = getExamDate(medicalOrder.plannedDate)
        const registeredAt =
          medicalOrder.registeredAt ?? order.registeredAt ?? examDate
        const ccamCodes = getUniqueCotationCodes(exam.cotation_ccam)
        const ngapCodes = getUniqueCotationCodes(exam.cotation_ngap)

        if (ccamCodes.length) {
          const cacheKey = `CCAM:${examDate.toISOString()}:${ccamCodes.join(
            ',',
          )}`
          if (!ccamCache.has(cacheKey)) {
            ccamCache.set(
              cacheKey,
              dispatch(findCcam({ codes: ccamCodes, examDate })).unwrap(),
            )
          }
          const ccamData = await ccamCache.get(cacheKey)

          generated.push(
            ...createCCAMPrecotations({
              codes: ccamCodes,
              ccamData: ccamData ?? [],
              medicalOrderId: medicalOrder.id,
              registeredAt,
            }),
          )
        }

        if (ngapCodes.length) {
          const cacheKey = `NGAP:${examDate.toISOString()}:${ngapCodes.join(
            ',',
          )}`
          if (!ngapCache.has(cacheKey)) {
            ngapCache.set(
              cacheKey,
              dispatch(findNgap({ codes: ngapCodes, examDate })).unwrap(),
            )
          }
          const ngapData = await ngapCache.get(cacheKey)

          generated.push(
            ...createNGAPPrecotations({
              codes: ngapCodes,
              ngapData: ngapData ?? [],
              medicalOrderId: medicalOrder.id,
              registeredAt,
            }),
          )
        }
      }

      return [...loadedPrecotations, ...generated]
    },
    [dispatch, order.medicalOrders, order.registeredAt],
  )

  // Load precotations for all medical orders with prices
  const loadPrecotations = useCallback(async () => {
    setIsLoading(true)
    try {
      const loadedPrecotations = await BillingService.loadPrecotationsForOrder(
        order.id,
      )
      const allPrecotations = await ensureInitialPrecotationsPerMedicalOrder(
        loadedPrecotations,
      )
      dispatch(setPrecotation(allPrecotations))
    } catch (err) {
      dispatch(
        enqueueSnackbar({
          message: 'Erreur lors du chargement des cotations',
          options: { variant: 'error' },
        }),
      )
    } finally {
      setIsLoading(false)
    }
  }, [dispatch, ensureInitialPrecotationsPerMedicalOrder, order.id])

  const ensureAllPrecotations = useCallback(async (): Promise<boolean> => {
    try {
      const loaded = await BillingService.loadPrecotationsForOrder(order.id)
      const result = await ensureInitialPrecotationsPerMedicalOrder(loaded)
      const nonFt = result.filter((p) => p.cotation_type !== 'FT')
      if (nonFt.length === 0) return false
      if (result.length === loaded.length) return true

      const saved = await BillingService.savePrecotationsForOrder(
        order.id,
        nonFt,
      )
      dispatch(setPrecotation(saved))
      return saved.filter((p) => p.cotation_type !== 'FT').length > 0
    } catch {
      return false
    }
  }, [dispatch, ensureInitialPrecotationsPerMedicalOrder, order.id])

  const savePrecotations = useCallback(
    async (rowsToSave?: PrecotationDto[]) => {
      setIsValidating(true)
      try {
        const source = (rowsToSave || precotations).filter(
          (row) => row.cotation_type !== 'FT',
        )
        const fallbackDate = order.registeredAt
          ? moment(order.registeredAt).format('YYYY-MM-DD')
          : undefined

        const saved = await BillingService.savePrecotationsForOrder(
          order.id,
          source,
          fallbackDate,
        )
        dispatch(setPrecotation(saved))

        dispatch(
          enqueueSnackbar({
            message: 'Cotations enregistrées',
            options: { variant: 'success' },
          }),
        )
        return saved
      } catch (err) {
        dispatch(
          enqueueSnackbar({
            message: 'Erreur lors de la sauvegarde des cotations',
            options: { variant: 'error' },
          }),
        )
        return []
      } finally {
        setIsValidating(false)
      }
    },
    [dispatch, order.id, order.registeredAt, precotations],
  )

  const validatePrecotations = useCallback(async () => {
    setIsValidating(true)
    try {
      await dispatch(doValidatePrecotations(precotations)).unwrap()
    } finally {
      setIsValidating(false)
    }
  }, [dispatch, precotations])

  const handleBillModeClick = useCallback(
    async (mode: number, rowsToSave?: PrecotationDto[]) => {
      const savedPrecotations = await savePrecotations(rowsToSave)
      const validationSource = savedPrecotations.length
        ? savedPrecotations
        : rowsToSave ?? precotations
      setIsValidating(true)
      setBillMode(mode.toString())

      if (shouldUpdateSituationAmo(mode)) {
        try {
          const _readerId = await getReaderId()
          if (!order.id || !_readerId || !memberSituation) {
            setIsValidating(false)
            return
          }
          const url = await BillingService.getCodeSituationSelector(
            order.id,
            _readerId,
            memberSituation.idJfse,
          )
          setCodeSituationSelectorUrl(url)
          setIsValidating(false)
          return
        } catch (e) {
          setIsValidating(false)
          dispatch(
            enqueueSnackbar({
              message:
                'Erreur lors de la récupération de l’url de la situation',
              options: { variant: 'error' },
            }),
          )
          return
        }
      }

      await dispatch(doValidatePrecotations(validationSource))
      setIsValidating(false)
    },
    [
      savePrecotations,
      getReaderId,
      dispatch,
      order.id,
      memberSituation,
      precotations,
    ],
  )

  const handleCreateBill = useCallback(async () => {
    if (!order.visitId || !memberSituation) return

    setIsCreatingBill(true)
    try {
      const _readerId = await getReaderId()
      const isApcv = await isReadingTypeApcv()

      const dto: CreateBillDTO = {
        idLecteur: _readerId,
        idResip: memberSituation.idJfse,
        medicalOrderIds: order.medicalOrders.map((mo) => mo.id),
        idVisit: order.visitId,
        idOrder: order.id,
        securisationFacture: billMode,
        apcv: isApcv,
      }

      const res = await dispatch(doCreateBill(dto)).unwrap()
      setBillUrl(res.url)
      dispatch(setValidation(null))
    } catch (e) {
      dispatch(
        enqueueSnackbar({
          message: 'Erreur lors de la création de la facture',
          options: { variant: 'error' },
        }),
      )
    } finally {
      setIsCreatingBill(false)
    }
  }, [
    dispatch,
    getReaderId,
    isReadingTypeApcv,
    order,
    memberSituation,
    billMode,
  ])

  const handleBillSuccess = useCallback(
    async (data: any) => {
      try {
        const _readerId = await getReaderId()
        if (!memberSituation || !_readerId || !order.visitId) return

        const res = await BillingService.finalizeBill({
          data,
          idResip: memberSituation.idJfse,
          idLecteur: _readerId,
          medicalOrderIds: order.medicalOrders.map((mo) => mo.id),
          idVisit: order.visitId,
          idOrder: order.id,
          idMemberSituation: memberSituation.identifiant,
        })
        if (res.Erreur) {
          dispatch(
            enqueueSnackbar({
              message: res.Error,
              options: { variant: 'error' },
            }),
          )
          return
        }

        dispatch(
          enqueueSnackbar({
            message: 'Facture créée avec succès',
            options: { variant: 'success' },
          }),
        )
        setBillUrl('')
        console.log('this is the response : ')
        console.log(res)
        const invoices: any[] = Array.isArray(res.invoice)
          ? res.invoice
          : res.invoice
          ? [res.invoice]
          : []

        const invoicesWithBalance = invoices
          .map((inv) => {
            const total = inv.patient_total ?? 0
            const payed = inv.patient_payed ?? 0
            const balance = total - payed
            return { ...inv, patient: { id: inv.id_patient }, balance }
          })
          .filter((inv) => inv.balance > 0)

        const allCovered = invoicesWithBalance.length === 0
        onBillSuccess?.({
          invoices: invoicesWithBalance,
          shouldShowBillStatusDialog: !allCovered,
          autoSetPaid: allCovered,
        })
      } catch (e) {
        dispatch(
          enqueueSnackbar({
            message: 'Erreur lors de la finalisation de la facture',
            options: { variant: 'error' },
          }),
        )
      }
    },
    [dispatch, getReaderId, memberSituation, order, onBillSuccess],
  )

  // Handle invoice only (upfront payment)
  const handleInvoiceOnly = useCallback(async () => {
    const ms = await ensureMemberSituation()

    if (!ms) {
      dispatch(
        enqueueSnackbar({
          message:
            'Situation du praticien introuvable. Mettez à jour la situation pour facturer.',
          options: { variant: 'error' },
        }),
      )
      return false
    }

    if (!order.visitId) {
      dispatch(
        enqueueSnackbar({
          message: 'Impossible d’ouvrir le paiement: visite introuvable',
          options: { variant: 'error' },
        }),
      )
      return false
    }

    if (!myCashRegister) {
      dispatch(
        enqueueSnackbar({
          message:
            'Vous devez ouvrir une caisse avant de procéder à un paiement',
          options: { variant: 'error' },
        }),
      )
      return false
    }

    // Check if precotations exist
    const hasPrecots = await ensureAllPrecotations()
    if (!hasPrecots) {
      dispatch(
        enqueueSnackbar({
          message:
            'Aucune cotation disponible. Ajoutez/enregistrez une cotation avant de facturer.',
          options: { variant: 'error' },
        }),
      )
      return false
    }

    // If invoice already exists, return true to open payment
    if (invoice) {
      onInvoiceCreated?.()
      return true
    }

    // Create invoice
    const dto: CreateInvoiceOnlyDto = {
      idMemberSituation: ms.identifiant,
      medicalOrderIds: order.medicalOrders.map((mo) => mo.id),
      idVisit: order.visitId,
      idOrder: order.id,
    }

    try {
      await dispatch(doCreateInvoiceOnly(dto)).unwrap()
      await dispatch(getInvoice({ orderId: order.id })).unwrap()
      onInvoiceCreated?.()
      return true
    } catch {
      dispatch(
        enqueueSnackbar({
          message: 'Une erreur est survenue lors de la création de la facture',
          options: { variant: 'error' },
        }),
      )
      return false
    }
  }, [
    dispatch,
    ensureMemberSituation,
    ensureAllPrecotations,
    order,
    myCashRegister,
    invoice,
    onInvoiceCreated,
  ])

  const addPrecotationRow = useCallback(
    (medicalOrderId: number) => {
      const medicalOrder = order.medicalOrders.find(
        (mo) => mo.id === medicalOrderId,
      )
      if (!medicalOrder) return

      const cotationType = getUniqueCotationCodes(
        medicalOrder.exam.cotation_ccam,
      ).length
        ? 'CCAM'
        : 'NGAP'
      const newRow = createPrecotationRow({
        medicalOrderId,
        cotationType,
        registeredAt: order.registeredAt,
      })

      dispatch(setPrecotation([...precotations, newRow]))
    },
    [dispatch, order, precotations],
  )

  const deletePrecotationRow = useCallback(
    async (precotation: PrecotationDto) => {
      try {
        if (precotation.id) {
          await dispatch(
            doRemovePrecotation({
              id: precotation.id,
              medicalOrderId: precotation.id_medical_order,
            }),
          ).unwrap()
        }

        dispatch(removeUnfinishedPrecot(precotation))
      } catch (e) {
        dispatch(
          enqueueSnackbar({
            message: 'Erreur lors de la suppression de la cotation',
            options: { variant: 'error' },
          }),
        )
      }
    },
    [dispatch],
  )

  // Close summary dialog
  const closeSummary = useCallback(() => {
    dispatch(setValidation(null))
  }, [dispatch])

  const billModesOptions = useMemo(() => {
    return BillModes.map((item) => {
      return {
        value: item.value,
        label: item.label,
        disabled: item.requiredCards.some(
          (cardType) =>
            !cardReaderInsertedCards.some((card) => card.type === cardType),
        ),
      }
    })
  }, [cardReaderInsertedCards])

  return {
    // State
    isLoading,
    isValidating,
    isCreatingBill,
    billUrl,
    codeSituationSelectorUrl,
    billMode,
    billModesOptions,
    // Actions
    loadPrecotations,
    savePrecotations,
    validatePrecotations,
    handleBillModeClick,
    handleCreateBill,
    handleBillSuccess,
    handleInvoiceOnly,
    addPrecotationRow,
    deletePrecotationRow,

    // Setters
    setBillUrl,
    setCodeSituationSelectorUrl,
    closeSummary,

    // Helpers
    ensureMemberSituation,
    ensureAllPrecotations,
  }
}

export default useBillingWorkflow
